import { useReducer, useCallback } from 'react';
import { Slide } from 'react-awesome-reveal';
import { Button, Input } from '@heroui/react';
import { useAutoFocus } from '@/shared/components';
import PasswordChecklist from 'react-password-checklist';
import { keyPressedHandler } from '@livefree/react-ui';

const reducer = (state, action) => {
  switch (action.type) {
    case 'message':
      return { ...state, message: action.payload };
    case 'password1':
      return { ...state, password1: action.payload };
    case 'password2':
      return { ...state, password2: action.payload };
    case 'valid':
      return { ...state, valid: action.payload };
    default:
      return state;
  }
};

export const PasswordForm = ({ label, description, errorMessage, isLoading, onComplete }) => {
  const [form, dispatch] = useReducer(reducer, {
    message: '',
    password1: '',
    password2: '',
  });

  useAutoFocus('password1');

  const handleComplete = useCallback(() => {
    if (form.valid && form.password1) {
      onComplete(form.password1);
    }
  }, [form.valid, form.password1, onComplete]);

  return (
    <Slide direction="right" duration={500}>
      <div className="flex flex-col justify-center w-full gap-4">
        <span className="text-default-500">{description}</span>
        <form className="flex flex-col gap-4">
          <Input
            type="password"
            id="password1"
            name="password1"
            label="Password"
            value={form.password1}
            onValueChange={x => dispatch({ type: 'password1', payload: x })}
            onKeyDown={keyPressedHandler('enter', handleComplete)}
            classNames={{
              inputWrapper:
                'border-transparent bg-white/10 dark:bg-white/10 group-data-[focus=true]:border-primary data-[hover=true]:border-foreground/20',
            }}
            variant="faded"
          />
          <Input
            type="password"
            id="password2"
            name="password2"
            label="Re-enter password"
            value={form.password2}
            onValueChange={x => dispatch({ type: 'password2', payload: x })}
            onKeyDown={keyPressedHandler('enter', handleComplete)}
            errorMessage={errorMessage}
            classNames={{
              inputWrapper:
                'border-transparent bg-white/10 dark:bg-white/10 group-data-[focus=true]:border-primary data-[hover=true]:border-foreground/20',
            }}
            variant="faded"
          />
        </form>
        {form.password2 && (
          <PasswordChecklist
            rules={['minLength', 'specialChar', 'number', 'capital', 'lowercase', 'match']}
            minLength={8}
            value={form.password1}
            valueAgain={form.password2}
            onChange={isValid => dispatch({ type: 'valid', payload: isValid })}
          />
        )}
        <div className="flex justify-end items-center">
          <Button
            color="primary"
            isDisabled={!form.valid}
            isLoading={isLoading}
            size="sm"
            onPress={handleComplete}
          >
            {label}
          </Button>
        </div>
      </div>
    </Slide>
  );
};
